import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../config';


export default function DoubtSolving() {
  const [doubts, setDoubts] = useState([]);
  const [selectedDoubt, setSelectedDoubt] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const [newDoubt, setNewDoubt] = useState({ subject: '', topic: '', text: '' });
  const [pastedImage, setPastedImage] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const chatEndRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || "{}");
  const isTeacher = user.role === 'teacher' || user.role === 'superadmin' || user.role === 'institute';
  const myName = user.name || "Unknown User";

  const fetchDoubts = useCallback(async () => {
    try {
      const url = isTeacher ? API_BASE_URL + '/api/doubts' : `${API_BASE_URL}/api/doubts?studentId=${user.penid || user.email}`;
      const res = await axios.get(url);
      setDoubts(res.data);
      
      setSelectedDoubt(currentSelected => {
        if (res.data.length > 0 && !currentSelected) {
          return res.data[0];
        } else if (currentSelected) {
          const updated = res.data.find(d => d.id === currentSelected.id);
          if (updated) return updated;
        }
        return currentSelected;
      });
      
    } catch (err) {
      console.error("Failed to fetch doubts", err);
    }
  }, [isTeacher, user.penid, user.email]);

  useEffect(() => {
    fetchDoubts();
  }, [fetchDoubts]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedDoubt]);

  const openNewDoubtModal = useCallback(() => {
    setSelectedDoubt(null);
    setMessageText("");
    setPastedImage(null);
    setShowNewModal(true);
  }, []);

  const uploadImage = async (file) => {
    setIsUploadingImage(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const title = selectedDoubt ? `doubt_${selectedDoubt.id}_${Date.now()}` : `new_doubt_${Date.now()}`;
      data.append('title', title);
      data.append('subject', selectedDoubt?.subject || newDoubt.subject || 'Doubt');
      data.append('type', 'IMAGE');

      const res = await axios.post(API_BASE_URL + '/api/files/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPastedImage(res.data.id);
      toast.success("Image attached!");
    } catch (err) {
      console.error("Image upload failed", err);
      toast.error("Failed to upload image.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          uploadImage(file);
        }
      }
    }
  };

  const handleSendReply = async () => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage) return;

    if (!selectedDoubt) {
      setNewDoubt((prev) => ({ ...prev, text: trimmedMessage }));
      setShowNewModal(true);
      return;
    }
    
    try {
      setIsSubmitting(true);
      const payload = {
        senderName: myName,
        senderRole: isTeacher ? "TEACHER" : "STUDENT",
        text: messageText,
        imageUrl: pastedImage
      };
      await axios.post(`${API_BASE_URL}/api/doubts/${selectedDoubt.id}/reply`, payload);
      setMessageText("");
      setPastedImage(null);
      fetchDoubts();
    } catch (err) {
      console.error(err);
      toast.error("Failed to send message.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEscalate = async () => {
    if (!selectedDoubt || !isTeacher) return;
    try {
      await axios.put(`${API_BASE_URL}/api/doubts/${selectedDoubt.id}/status`, { responder: myName, status: "In Progress" });
      fetchDoubts();
    } catch (err) { console.error(err); }
  };

  const handleResolve = async () => {
    if (!selectedDoubt || !isTeacher) return;
    try {
      await axios.put(`${API_BASE_URL}/api/doubts/${selectedDoubt.id}/status`, { status: "Resolved" });
      fetchDoubts();
    } catch (err) { console.error(err); }
  };

  const handleDeleteThread = async () => {
    if (!selectedDoubt) return;
    const confirmed = window.confirm("Delete this query thread permanently?");
    if (!confirmed) return;
    console.log("Deleting doubt:", selectedDoubt.id);
    try {
      await axios.delete(`${API_BASE_URL}/api/doubts/${selectedDoubt.id}`);
      toast.success("Doubt thread deleted");
      setSelectedDoubt(null);
      fetchDoubts();
    } catch (err) {
      console.error("Failed to delete doubt:", err);
      const serverMessage =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" && err.response.data) ||
        err?.response?.statusText ||
        err?.message ||
        "Could not delete thread";
      toast.error(serverMessage);
    }
  };

  const handleCreateDoubt = async () => {
    if (!newDoubt.subject || (!newDoubt.text && !pastedImage)) return;
    setIsSubmitting(true);
    try {
      const payload = {
        studentId: user.penid || user.email,
        studentName: myName,
        subject: newDoubt.subject,
        topic: newDoubt.topic,
        messages: [{
            text: newDoubt.text,
            imageUrl: pastedImage
        }]
      };
      await axios.post(API_BASE_URL + '/api/doubts', payload);
      setShowNewModal(false);
      setNewDoubt({ subject: '', topic: '', text: '' });
      setMessageText("");
      setPastedImage(null);
      fetchDoubts();
    } catch (err) { 
      console.error(err);
      toast.error("Failed to create doubt.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col h-[calc(100vh-8rem)]">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
              AI Doubt Resolution Center
            </span>
          </h1>
          <p className="mt-2 text-sm text-slate-400 max-w-2xl">
            Real-time querying interface connecting students with 24/7 AI tutor bots and escalating complex queries to Human TAs.
          </p>
        </div>
        
        <div className="flex gap-2">
          <div className="px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-3">
            <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              PenSense AI
            </span>
            <span className="text-slate-600">|</span>
            <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
              Human TA
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
        
        {/* Left Column: Active Doubts Queue */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-5 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-4">
             <h2 className="text-lg font-semibold text-slate-200">{isTeacher ? "Global Query Queue" : "My Doubts"}</h2>
             <div className="flex items-center gap-2">
                 <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500 text-xs font-bold">{doubts.length} Active</span>
                 {!isTeacher && (
                   <button onClick={openNewDoubtModal} className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-bold shadow">
                     + Ask Doubt
                   </button>
                 )}
             </div>
          </div>

          <div className="relative mb-4">
            <input type="text" placeholder="Search by student, ID, or subject..." className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 pl-10 text-sm text-white focus:outline-none focus:border-yellow-500/50 transition-colors" />
            <svg className="absolute left-3 top-2.5 text-slate-500" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          </div>
          
          <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
            {doubts.map((doubt, idx) => (
              <div key={idx} onClick={() => setSelectedDoubt(doubt)} className={`p-4 rounded-xl border cursor-pointer hover:bg-slate-800/60 transition-colors ${
                selectedDoubt?.id === doubt.id ? 'bg-slate-800/80 border-yellow-500/30' : 'bg-slate-800/30 border-slate-700/50'
              }`}>
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-400">{doubt.doubtIdStr}</span>
                  <span className="text-[10px] text-slate-500">{doubt.timeAgo}</span>
                </div>
                
                <h3 className="font-medium text-slate-200 text-sm mb-1">{doubt.studentName}</h3>
                <p className="text-xs text-slate-400 font-medium mb-3">{doubt.subject} • {doubt.topic}</p>
                
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded inline-block ${
                    doubt.status === 'Unresolved' ? 'bg-rose-500/10 text-rose-400' : 
                    doubt.status === 'In Progress' ? 'bg-sky-500/10 text-sky-400' : 
                    'bg-emerald-500/10 text-emerald-400'
                  }`}>
                    {doubt.status}
                  </span>
                  
                  {doubt.responder && (
                    <span className="text-[10px] text-slate-500 font-medium flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                      {doubt.responder}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {doubts.length === 0 && <div className="text-sm text-slate-500 p-4 text-center">No active doubts.</div>}
          </div>
        </div>

        {/* Right Column: Chat/Resolution Window */}
        <div className="lg:col-span-2 rounded-3xl border border-yellow-500/20 bg-slate-900/40 flex flex-col overflow-hidden relative">
          {/* Header */}
          <div className="p-5 border-b border-slate-800 bg-slate-900/60 flex items-center justify-between shrink-0">
            {selectedDoubt ? (
              <div>
                <h2 className="font-semibold text-slate-200">{selectedDoubt.doubtIdStr}: {selectedDoubt.topic}</h2>
                <p className="text-xs text-slate-400 mt-1">Student: {selectedDoubt.studentName} • Context: {selectedDoubt.subject}</p>
              </div>
            ) : (
              <div>
                <h2 className="font-semibold text-slate-400">Select a Query</h2>
              </div>
            )}
            
            {selectedDoubt && (
              <div className="flex gap-2">
                 {isTeacher && selectedDoubt.responder !== myName && (
                   <button onClick={handleEscalate} className="px-3 py-1.5 text-xs font-medium text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 rounded-lg border border-orange-500/20 transition-colors">
                     Escalate to TA
                   </button>
                 )}
                 {isTeacher && (
                   <button onClick={handleResolve} className="px-3 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg border border-emerald-500/20 transition-colors">
                     Mark Resolved
                   </button>
                 )}
                 <button onClick={handleDeleteThread} className="px-3 py-1.5 text-xs font-medium text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 rounded-lg border border-rose-500/20 transition-colors">
                   Delete Thread
                 </button>
              </div>
            )}
            <button onClick={openNewDoubtModal} className="px-3 py-0.5 text-xs font-semibold text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-full border border-emerald-500/20 ml-3">
              Start new query
            </button>
          </div>

          {/* Chat Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
             {!selectedDoubt ? (
               <div className="flex flex-col items-center justify-center h-full text-slate-500">
                 <svg className="w-16 h-16 mb-4 opacity-20" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                 <p>Select a query from the left menu to view thread.</p>
               </div>
             ) : (
               selectedDoubt.messages?.map((msg, i) => {
                 const isMe = msg.senderName === myName;
                 const isAI = msg.senderRole === 'AI';
                 
                 if (isAI) {
                   return (
                     <div key={i} className="flex items-start gap-4 flex-row-reverse">
                       <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-orange-500/20">
                         <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a2 2 0 0 1 2 2c0 1.1-.9 2-2 2H8a5 5 0 0 0-5 5v3a5 5 0 0 0 5 5h8a5 5 0 0 0 5-5v-3c0-1.1.9-2 2-2"></path><path d="M9 16.5A2.5 2.5 0 0 0 11.5 19h1A2.5 2.5 0 0 0 15 16.5"></path></svg>
                       </div>
                       <div className="flex flex-col items-end">
                         <div className="flex items-center gap-2 mb-1">
                           <span className="text-[10px] text-slate-500">{msg.timestamp}</span>
                           <span className="font-medium text-orange-400 text-sm">{msg.senderName}</span>
                         </div>
                          <div className="p-4 rounded-2xl rounded-tr-sm bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-orange-500/20 text-sm text-slate-300">
                            {msg.imageUrl && (
                              <div className="mb-3">
                                <img 
                                  src={`${API_BASE_URL}/api/files/view/${msg.imageUrl}`} 
                                  alt="Doubt attachment" 
                                  className="max-w-full rounded-lg border border-orange-500/20"
                                />
                              </div>
                            )}
                            {msg.text}
                          </div>
                       </div>
                     </div>
                   );
                 }
                 
                 return (
                   <div key={i} className={`flex items-start gap-4 ${isMe ? 'flex-row-reverse' : ''}`}>
                     <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0 ${isMe ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-300'}`}>
                       {msg.senderName?.substring(0,2).toUpperCase()}
                     </div>
                     <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                       <div className={`flex items-center gap-2 mb-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                         <span className="font-medium text-slate-200 text-sm">{isMe ? 'You' : msg.senderName}</span>
                         <span className="text-[10px] text-slate-500">{msg.timestamp}</span>
                       </div>
                        <div className={`p-4 rounded-2xl text-sm border ${
                          isMe 
                            ? 'bg-indigo-600/20 border-indigo-500/30 text-indigo-100 rounded-tr-sm' 
                            : 'bg-slate-800/60 border-slate-700 text-slate-300 rounded-tl-sm'
                        }`}>
                          {msg.imageUrl && (
                            <div className="mb-3">
                              <img 
                                src={`${API_BASE_URL}/api/files/view/${msg.imageUrl}`} 
                                alt="Doubt attachment" 
                                className="max-w-full rounded-lg border border-slate-700"
                              />
                            </div>
                          )}
                          {msg.text}
                        </div>
                     </div>
                   </div>
                 );
               })
             )}
             <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-slate-900 border-t border-slate-800 shrink-0">
          <div className="relative flex flex-col">
          {pastedImage && (
            <div className="mb-2 p-2 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src={`${API_BASE_URL}/api/files/view/${pastedImage}`} 
                  alt="Preview" 
                  className="w-12 h-12 object-cover rounded-lg border border-slate-700"
                />
                <span className="text-xs text-slate-400">Image attached</span>
              </div>
              <button 
                onClick={() => setPastedImage(null)}
                className="p-1 hover:bg-slate-800 rounded-full text-slate-500 hover:text-rose-400 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
          )}

          <div className="relative flex items-center">
            <div className="absolute left-3 flex items-center gap-2">
              {isUploadingImage ? (
                <div className="w-5 h-5 border-2 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin"></div>
              ) : (
                <button className="text-slate-500 hover:text-slate-300" onClick={() => toast.info("Paste an image from clipboard directly into this box!")}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7"></path><line x1="16" y1="5" x2="22" y2="5"></line><line x1="19" y1="2" x2="19" y2="8"></line><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path></svg>
                </button>
              )}
            </div>
            <input 
              type="text" 
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendReply()}
              onPaste={handlePaste}
              placeholder={
                selectedDoubt
                  ? selectedDoubt.status === 'Resolved'
                    ? "This thread is resolved."
                    : "Type a response or paste image..."
                  : "Type a question or paste image to start"
              }
              className="w-full bg-slate-950 border border-slate-800 rounded-xl py-3 pl-10 pr-12 text-sm text-white focus:outline-none focus:border-yellow-500/50 transition-colors disabled:opacity-50"
            />
            <button
            onClick={handleSendReply}
            disabled={
              isSubmitting || (
              selectedDoubt
                ? selectedDoubt.status === 'Resolved' || (!messageText.trim() && !pastedImage)
                : (!messageText.trim() && !pastedImage))
            }
            className="absolute right-3 bg-gradient-to-r from-yellow-500 to-orange-500 p-1.5 rounded-lg text-white shadow-lg shadow-orange-500/20 hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
          >
            {isSubmitting ? (
               <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            )}
          </button>
          </div>
          </div>
        </div>
      </div>
    </div>

      {/* New Doubt Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 p-6 rounded-2xl w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-4">Submit a New Doubt</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Subject</label>
                <input 
                  type="text" value={newDoubt.subject} onChange={e => setNewDoubt({...newDoubt, subject: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white" placeholder="e.g. Physics"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Topic</label>
                <input 
                  type="text" value={newDoubt.topic} onChange={e => setNewDoubt({...newDoubt, topic: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white" placeholder="e.g. Right Hand Rule"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Your Question</label>
                <textarea 
                  value={newDoubt.text} 
                  onChange={e => setNewDoubt({...newDoubt, text: e.target.value})}
                  onPaste={handlePaste}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white h-32 resize-none" 
                  placeholder="Describe where you're stuck... (You can also paste images)"
                ></textarea>
              </div>
              {pastedImage && (
                <div className="p-2 border border-slate-800 rounded-xl bg-slate-950/50 flex items-center gap-3">
                  <img src={`${API_BASE_URL}/api/files/view/${pastedImage}`} className="h-10 w-10 object-cover rounded" alt="pasted" />
                  <span className="text-[10px] text-slate-500 font-medium italic">Image attached</span>
                  <button onClick={() => setPastedImage(null)} className="ml-auto text-rose-500 text-xs">Remove</button>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setShowNewModal(false)}
                className="flex-1 px-4 py-2 border border-slate-700 text-slate-300 hover:bg-slate-800 rounded-xl font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleCreateDoubt}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-medium shadow-lg shadow-emerald-900/20 disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={isSubmitting || !newDoubt.subject || (!newDoubt.text && !pastedImage)}
              >
                {isSubmitting && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                {isSubmitting ? "Routing..." : "Submit Route to AI"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
